import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Text,
  View,
  Platform,
  StyleSheet,
  Dimensions,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { translate } from 'react-i18next';
import { Field, reduxForm } from 'redux-form';
import {
  TextInput,
  FormHeader,
  FormError,
  showMessage,
} from '../ui';
import {
  Button,
  ButtonSize,
} from '../components';
import Theme from '../theme';
import { UserServiceClient } from 'fitbeat.api-clients';
import { getApiConfig } from '../config';
import { MessagingButtons, Divider } from '../ui';

import { contactUs } from '../ui/components/Toast';
import { Logger } from '../utils/Logger';
import { isRequired } from '../utils/validator';
import { GoogleAnalytics } from '../services/google-analytics';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import UserDataConsumer from '../utils/UserDataConsumer';
import { defaultScale } from '../utils/scaling';
import { build } from '../config';
import { openLink } from '../utils/linkUtils';

const isElite = build.variant.isElite;

class ContactScreen extends Component {
  googleAnalytics = new GoogleAnalytics();
  userServiceClient = new UserServiceClient(getApiConfig(), fetch, null, new AuthTokenBuilder(), this.googleAnalytics);
  state = {
    fetchingLinkError: false,
    facebookMessengerLink: null,
    whatsAppLink: null,
    contactUsFormSubmitted: false,
    showThankYouScreen: false,
    submitError: false,
    message: '',
    isLoading: false,
  };

  componentDidMount = async () => {
    this.googleAnalytics.setCurrentScreen('ContactScreen');
    try {
      this.setState({ fetchingMessagingLinks: true });

      const messagingData = await Promise.all([
        this.userServiceClient.getUserMessengerPlatformLink('FacebookMessenger'),
        this.userServiceClient.getUserMessengerPlatformLink('WhatsApp'),
      ]);

      this.setState({
        facebookMessengerLink: messagingData[0].platformRedirectLink,
        whatsAppLink: messagingData[1].platformRedirectLink,
      });
    } catch (e) {
      this.setState({ fetchingLinkError: true });
    } finally {
      this.setState({ fetchingMessagingLinks: false });
    }
  }

  setMessage(value) {
    this.setState({ message: value });
  }

  buildPromiseToBeHandled(message) {
    const userProfile = this.props.user.userProfile;

    function request(report) {
      report.context = 'USER FEEDBACK';
      report.errorClass = `USER FEEDBACK from ${userProfile.email} on ${new Date().toLocaleString()}`;
      let userDetails = `Unknown user`;
      if (userProfile) {
        userDetails = `User details:\nid:${userProfile.uid},email:${userProfile.email},name:${userProfile.fullname}`;
      }
      report.errorMessage = `${message}\n\n${userDetails}`;
    }

    if (Platform.OS === 'web') {
      return { beforeSend: request };
    } else {
      return request;
    }
  }

  submit = async (values) => {
    const { error: message } = values;

    if (!message) {
      return;
    }

    try {
      this.setState({ isLoading: true });
      Logger.notify(new Error(message), this.buildPromiseToBeHandled(message));
      await this.userServiceClient.contactUs(message);
      if (this.state.fetchingLinkError) {
        showMessage(this.props.t('contactUsThanks'));
      } else {
        this.setState({ showThankYouScreen: true });
      }
      this.props.reset();
    } catch (e) {
      this.handleError(e, 'Error submitting contact us form');
      this.setState({ submitError: true });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  handleError = (error, caller = '') => {
    const { userProfile } = this.props.user;
    Logger.notify(error, (report) => {
      report.errorClass = 'ContactUsScreen';
      const userDetails = `User id :${userProfile.uid}`;
      report.errorMessage = `Caller: ${caller}\n\nuserDetails: ${userDetails}`;
    });
  }

  openMessagingApp = (link) => {
    try {
      openLink(link);
    } catch (e) {
      showMessage(this.props.t('openMessagingAppFailed'));
      this.handleError(e, `Failed to open messaging app ${link}`);
    }
  }

  messagingContent = () => {
    const showLoadingIndicator = this.state.fetchingMessagingLinks && !this.state.fetchingLinkError;
    const showMessagingButtons = (this.state.facebookMessengerLink || this.state.whatsAppLink) && !this.state.fetchingLinkError && !this.state.message;
    if (showLoadingIndicator) {
      return (
        <ActivityIndicator
          size='small'
          color={Theme.PRIMARY}
          animating={true}
          style={styles.loadingIndicator}
        />
      );
    } else if (showMessagingButtons) {
      return (
        <>
          <Divider text={'OR'}
                   styles={{
                     paddingVertical: defaultScale(25),
                   }}/>
          <MessagingButtons facebookMessengerLink={this.state.facebookMessengerLink}
                            whatsAppLink={this.state.whatsAppLink}
                            openMessagingApp={this.openMessagingApp} disabled={false} />
        </>
      );
    }
  }

  contactUsContent() {
    const {
      t,
      handleSubmit,
    } = this.props;
    const isSmallMobile = Dimensions.get('screen').height < 670;
    return (
      <>
        {Platform.OS == 'web' &&
          <FormHeader title={t('contactUs')} style={styles.header} />
        }

        <Text style={styles.subHeading}>{t('contactUsSubHeading')}</Text>

        <Field
          multiline
          numberOfLines={isSmallMobile ? 3 : 4}
          maxLength={defaultScale(200)}
          name='error'
          returnKeyLabel='return'
          component={TextInput}
          autoCapitalize='none'
          keyboardType='default'
          placeholder='Your message...'
          validate={isRequired}
          showLabel={Platform.OS !== 'web'}
          onChange={(_, value) => this.setMessage(value)}
        />

        {
          this.state.submitError &&
          <FormError error={{ message: t('contactUsError') }} />
        }
          <Button
            title='Send'
            isLoading={this.state.isLoading}
            fontColor={Theme.WHITE}
            size={ButtonSize.MEDIUM}
            containerStyle={styles.button}
            onPress={handleSubmit(this.submit)}
          />
        {!isElite && this.messagingContent()}
      </>
    );
  }

  thankYouContent() {
    const { t } = this.props;
    return (
      <View style={{
        flex: 1,
        justifyContent: 'center',
      }}>
        <Text style={styles.heading}>{t('contactUsThanks')}</Text>
        {!isElite && <MessagingButtons facebookMessengerLink={this.state.facebookMessengerLink}
                                       whatsAppLink={this.state.whatsAppLink}
                                       openMessagingApp={this.openMessagingApp}
        />}
      </View>
    );
  }

  render() {
    const { isToast } = this.props;

    if (isToast) {
      return null;
    }

    return (
      <ScrollView contentContainerStyle={[styles.container]}>
        {this.state.showThankYouScreen ? this.thankYouContent() : this.contactUsContent()}
      </ScrollView>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: defaultScale(15),
    maxWidth: defaultScale(800),
    backgroundColor: Theme.WHITE,
    ...Platform.select({
      web: {
        paddingVertical: 15,
        paddingHorizontal: 30,
        height: '100%',
      },
    }),
  },
  subHeading: {
    paddingBottom: defaultScale(10),
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_DARK_MEDIUM,
  },
  buttonContainer: {
    ...Platform.select({
      web: {
        alignSelf: 'flex-end',
      },
    }),
  },
  button: {
    ...Platform.select({
      web: {
        paddingHorizontal: 20,
      },
    }),
    marginTop: defaultScale(3),
  },
  header: {
    ...Theme.textStyle.heading,
    textAlign: 'left',
    paddingVertical: 0,
  },
  loadingIndicator: {
    flex: 1,
    justifyContent: 'center',
  },
  heading: {
    ...Theme.textStyle.heading,
    fontSize: defaultScale(28),
    paddingTop: defaultScale(30),
    paddingBottom: defaultScale(25),
    color: Theme.BLACK,
  },
  messagingButtonsContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
});

const mapStateToProps = (state) => {
  const { toast } = state.ui;

  return {
    isToast: !!toast,
  };
};

ContactScreen = connect(mapStateToProps)(ContactScreen);
ContactScreen = UserDataConsumer(ContactScreen);
ContactScreen = translate(['contactUs'], { wait: true })(ContactScreen);
ContactScreen = reduxForm({ form: 'ContactScreenForm' })(ContactScreen);
export default ContactScreen;
